import {
  requestPresignedUrls,
  uploadFilesInParallel,
} from '../services/common.service';
import {
  type FileUploadItem,
  type PresignedUrl,
  type UploadError,
  type UploadProgress,
} from '../types/common.type';

// Upload state type
export interface UploadState {
  files: FileUploadItem[];
  abortController: AbortController | null;
  isUploading: boolean;
}

// Upload options type
export interface UploadOptions {
  maxConcurrentUploads?: number;
  onProgress?: (_fileId: string, _progress: UploadProgress) => void;
  onError?: (_error: UploadError) => void;
}

/**
 * Create initial upload state
 */
export const createUploadState = (): UploadState => ({
  files: [],
  abortController: null,
  isUploading: false,
});

/**
 * Reset upload state
 */
export const resetUploadState = (state: UploadState): UploadState => {
  if (state.abortController) {
    state.abortController.abort();
  }
  return {
    files: [],
    abortController: null,
    isUploading: false,
  };
};

/**
 * Initialize files for upload
 */
export const initializeFiles = (files: File[]): FileUploadItem[] => {
  return files.map((file, index) => ({
    id: `file-${index}-${Date.now()}`,
    file,
    status: 'pending',
    progress: 0,
  }));
};

/**
 * Create upload error
 */
export const createUploadError = (code: UploadError['code'], message: string, details?: any): UploadError => {
  const error = new Error(message) as UploadError;
  error.code = code;
  error.details = details;
  return error;
};

/**
 * Request presigned URLs for files
 */
export const getPresignedUrls = async (files: File[]) => {
  try {
    const response = await requestPresignedUrls(files.map(item => item.name));
    if (!response.data) {
      throw createUploadError('PRESIGN_FAILED', response.message || 'Failed to get presigned URLs');
    }
    return response.data;
  } catch (error: any) {
    throw createUploadError('PRESIGN_FAILED', `Failed to request presigned URLs: ${error.message}`, error);
  }
};

/**
 * Map presigned URLs to file items
 */
export const mapPresignedUrlsToFiles = (files: FileUploadItem[], presignedUrls: PresignedUrl[]) => {
  if (presignedUrls.length !== files.length) {
    throw createUploadError('PRESIGN_FAILED', 'Mismatch between files and presigned URLs');
  }
  const updatedFiles = files.map((fileItem, index) => {
    const presigned = presignedUrls[index];
    if (!presigned) {
      throw createUploadError('PRESIGN_FAILED', `Missing presigned URL for file at index ${index}`);
    }
    return {
      ...fileItem,
      id: presigned.ulid,
      file_key: presigned.file_key,
      upload_url: presigned.upload_url,
    };
  });
  const uploadItems = updatedFiles.map(fileItem => ({
    file: fileItem.file,
    upload_url: fileItem.upload_url!,
    id: fileItem.id,
  }));
  return { updatedFiles, uploadItems };
};

/**
 * Update file progress
 */
export const updateFileProgress = (
  files: FileUploadItem[],
  fileId: string,
  progress: UploadProgress
): FileUploadItem[] => {
  return files.map(file => (file.id === fileId ? { ...file, progress: progress.percentage } : file));
};

/**
 * Mark files as uploading
 */
export const markFilesAsUploading = (files: FileUploadItem[]): FileUploadItem[] => {
  return files.map(file => ({ ...file, status: 'uploading' as const }));
};

/**
 * Mark files as completed
 */
export const markFilesAsCompleted = (files: FileUploadItem[]): FileUploadItem[] => {
  return files.map(file =>
    file.status === 'uploading' ? { ...file, status: 'completed' as const, progress: 100 } : file
  );
};

/**
 * Mark files as error
 */
export const markFilesAsError = (files: FileUploadItem[], errorMessage: string): FileUploadItem[] => {
  return files.map(file =>
    file.status === 'uploading' ? { ...file, status: 'error' as const, error: errorMessage } : file
  );
};

/**
 * Cancel uploads
 */
export const cancelUploads = (state: UploadState): UploadState => {
  if (state.abortController) {
    state.abortController.abort();
  }
  const cancelledFiles = state.files.map(file =>
    file.status === 'uploading' ? { ...file, status: 'error' as const, error: 'Upload cancelled' } : file
  );
  return {
    ...state,
    files: cancelledFiles,
    isUploading: false,
  };
};

/**
 * Get upload progress summary
 */
export const getUploadProgress = (files: FileUploadItem[]) => {
  const completed = files.filter(f => f.status === 'completed').length;
  const total = files.length;
  return {
    completed,
    total,
    percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
  };
};

/**
 * Extract file keys from completed uploads
 */
export const extractFileKeys = (files: FileUploadItem[]): string[] => {
  return files.filter(file => file.file_key && file.status === 'completed').map(file => file.id!);
};

/**
 * Perform parallel uploads with state updates
 */
export const performParallelUploads = async (
  uploadItems: { file: File; upload_url: string; id: string }[],
  files: FileUploadItem[],
  options: UploadOptions
): Promise<FileUploadItem[]> => {
  let currentFiles = markFilesAsUploading(files);
  try {
    await uploadFilesInParallel(
      uploadItems,
      (fileId, progress) => {
        currentFiles = updateFileProgress(currentFiles, fileId, progress);
        if (options.onProgress) {
          options.onProgress(fileId, progress);
        }
      },
      options.maxConcurrentUploads || 3
    );
    return markFilesAsCompleted(currentFiles);
  } catch (error: any) {
    const errorMessage = `File upload failed: ${error.message}`;
    const uploadError = createUploadError('UPLOAD_FAILED', errorMessage, error);
    if (options.onError) {
      options.onError(uploadError);
    }
    throw uploadError;
  }
};

/**
 * Main upload function - orchestrates the entire upload flow
 */
export const uploadFiles = async (files: File[], options: UploadOptions = {}): Promise<string[]> => {
  try {
    // Step 1: Initialize files
    const fileItems = initializeFiles(files);
    // Step 2: Request presigned URLs
    const presignedUrls = await getPresignedUrls(files);
    // Step 3: Map presigned URLs to files
    const { updatedFiles, uploadItems } = mapPresignedUrlsToFiles(fileItems, presignedUrls);
    // Step 4: Upload files in parallel
    const completedFiles = await performParallelUploads(uploadItems, updatedFiles, options);
    // Step 5: Extract and return file keys
    return extractFileKeys(completedFiles);
  } catch (error) {
    if (options.onError && error instanceof Error) {
      options.onError(error as UploadError);
    }
    throw error;
  }
};